import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { usePrefersTouch } from "../hooks/usePrefersTouch";

type Props = {
  /** Treść wyjaśnienia pokazywana w dymku. Pusty tekst = ikonka się nie renderuje. */
  text: string;
  /** Etykieta dostępności (aria-label) przycisku „?". */
  label?: string;
};

// Minimalny, strukturalny typ zdarzenia (repo nie ma @types/react — nie zależymy od typów Reacta).
type ClickLike = { preventDefault: () => void; stopPropagation: () => void };

const VIEWPORT_MARGIN = 8;
/** Zwłoka przy zamykaniu — tyle myszka ma na przejechanie ze szczeliny między wyzwalaczem a dymkiem. */
const CLOSE_DELAY_MS = 120;

/**
 * Mechanika dymka bez jego treści i bez wyzwalacza: stan, pozycjonowanie, nasłuchy.
 *
 * Wydzielone z `HelpTooltip`, bo ten sam dymek jest potrzebny nad zwykłym tekstem w tabeli
 * (`TableCellText`), a nie tylko nad ikonką „?".
 *
 * Dymek leci portalem do <body> z pozycją fixed przyciętą do widoku, żeby nie obcinał go overflow
 * paneli. Na desktopie działa hover/focus, na dotyku klik przełącza — rozdzielone tryby, inaczej
 * hover i klik na dotyku wygaszają się nawzajem. Wzorzec z UrmateEspoCrmAiAssistant/HelpTooltip.
 */
export function useTooltip(options: { wide?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const prefersTouch = usePrefersTouch();
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const cancelClose = useCallback(() => {
    if (closeTimer.current === null) return;
    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  const close = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);
  const closeSoon = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);
  const openNow = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  // Licznik zamknięcia nie może przeżyć odmontowania komponentu (wiersze tabeli znikają przy stronicowaniu).
  useEffect(() => cancelClose, [cancelClose]);

  // Pozycjonowanie: nad wyzwalaczem, wyśrodkowane; flip pod spód gdy brak miejsca; clamp do widoku (obie osie).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const trigger = triggerRef.current;
    const tip = tooltipRef.current;
    if (!trigger || !tip) return;
    const t = trigger.getBoundingClientRect();
    const box = tip.getBoundingClientRect();
    let top = t.top - box.height - VIEWPORT_MARGIN;
    if (top < VIEWPORT_MARGIN) top = t.bottom + VIEWPORT_MARGIN;
    const maxTop = window.innerHeight - box.height - VIEWPORT_MARGIN;
    if (top > maxTop) top = Math.max(VIEWPORT_MARGIN, maxTop);
    let left = t.left + t.width / 2 - box.width / 2;
    const maxLeft = window.innerWidth - box.width - VIEWPORT_MARGIN;
    if (left > maxLeft) left = maxLeft;
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    setPos({ top, left });
  }, [open]);

  // Zamknij przy scrollu/resize (pozycja fixed by się rozjechała), przy kliknięciu poza (dotyk) i na Escape.
  useEffect(() => {
    if (!open) return;
    const onScroll = (e: Event) => {
      // Przewijanie wewnątrz samego dymka go nie zamyka — inaczej długiej treści nie dałoby się doczytać.
      const target = e.target as Node | null;
      if (target && tooltipRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (target && triggerRef.current?.contains(target)) return;
      if (target && tooltipRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Dotyk: tylko klik przełącza (bez hover/focus, które kolidują z klikiem). Desktop: hover + focus.
  const triggerProps = prefersTouch
    ? {
        onClick: (e: ClickLike) => {
          // Wewnątrz <label> — nie pozwól, żeby klik w wyzwalacz przełączył powiązane pole (np. checkbox).
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        },
        "aria-expanded": open,
      }
    : {
        onMouseEnter: openNow,
        onMouseLeave: closeSoon,
        onFocus: openNow,
        onBlur: close,
        // Klik na desktopie nie steruje stanem (hover już to robi) — blokujemy tylko przełączenie pola.
        onClick: (e: ClickLike) => {
          e.preventDefault();
          e.stopPropagation();
        },
        "aria-describedby": open ? tooltipId : undefined,
      };

  const className = [
    "help-tooltip",
    prefersTouch ? "help-tooltip--touch" : "",
    options.wide ? "help-tooltip--wide" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderTooltip = (content: unknown) =>
    open
      ? createPortal(
          <span
            id={tooltipId}
            ref={tooltipRef}
            role={prefersTouch ? "dialog" : "tooltip"}
            className={className}
            style={{
              position: "fixed",
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
            // Najechanie na sam dymek trzyma go otwartym — bez tego nie da się w nim przewijać.
            onMouseEnter={prefersTouch ? undefined : cancelClose}
            onMouseLeave={prefersTouch ? undefined : closeSoon}
          >
            {content}
          </span>,
          document.body,
        )
      : null;

  return { triggerRef, triggerProps, renderTooltip };
}

/** Ikonka „?" z dymkiem wyjaśnienia dla pól ustawień panelu. */
export function HelpTooltip({ text, label = "Wyjaśnienie" }: Props) {
  if (!text.trim()) return null;
  return <HelpTooltipInner text={text} label={label} />;
}

function HelpTooltipInner({ text, label }: Required<Props>) {
  const { triggerRef, triggerProps, renderTooltip } = useTooltip();

  return (
    <span className="help-tooltip-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="help-tooltip-trigger"
        aria-label={label}
        {...triggerProps}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
        </svg>
      </button>
      {renderTooltip(text)}
    </span>
  );
}
